import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/hooks/useConfig";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, Plus, Trash2, Upload } from "lucide-react";

const pretty = s => s ? s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "";

export default function BrandFormPage() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const isAdmin = (user?.roles || []).some(r => ["Admin","admin"].includes(r));
  const canKyc = isAdmin || perms.includes("brands.view_kyc");
  const canContacts = isAdmin || perms.includes("brands.view_contacts");
  const canTerms = isAdmin || perms.includes("brands.view_terms");
  const { getItems } = useConfig();

  const [form, setForm] = useState({
    display_name: "", owner_type: "third_party", logo_url: "", categories: [],
    description: "", website: "", country_of_origin: "India", year_started: "",
    already_selling_in: [], already_present_note: "",
    legal_name: "", entity_type: "", gst_number: "", pan_number: "",
    fssai_number: "", fssai_expiry: "", trademark_number: "", registered_address: "",
    manufacturer_name: "", manufacturer_address: "", marketer_name: "", marketer_address: "",
    contacts: [{ contact_id: "", name: "", role: "", phone: "", email: "", is_primary: true }],
    bank_account_name: "", bank_account_number: "", bank_ifsc: "", bank_name: "", bank_branch: "",
    terms: {}, restrictions: { blocked_states: [], blocked_pincodes: "", blocked_prefixes: "", reason: "" },
  });
  const [brand, setBrand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({ identity: true, company: true, contacts: true, bank: false, terms: false, restrictions: false, documents: false });
  const [errors, setErrors] = useState([]);
  const isEdit = !!brandId;

  useEffect(() => {
    if (brandId) {
      api.get(`/brands/${brandId}`).then(r => {
        const b = r.data;
        setBrand(b);
        const id = b.identity || {};
        const co = b.company || {};
        const bk = b.bank || {};
        setForm({
          display_name: b.display_name || "", owner_type: id.owner_type || b.owner_type || "third_party",
          logo_url: id.logo_url || "", categories: id.categories || [],
          description: id.description || "", website: id.website || "",
          country_of_origin: id.country_of_origin || "India", year_started: id.year_started || "",
          already_selling_in: id.already_selling_in || [], already_present_note: id.already_present_note || "",
          legal_name: co.legal_name || "", entity_type: co.entity_type || "",
          gst_number: co.gst_number || "", pan_number: co.pan_number || "",
          fssai_number: co.fssai_number || "", fssai_expiry: co.fssai_expiry || "",
          trademark_number: co.trademark_number || "", registered_address: co.registered_address || "",
          manufacturer_name: co.manufacturer_name || "", manufacturer_address: co.manufacturer_address || "",
          marketer_name: co.marketer_name || "", marketer_address: co.marketer_address || "",
          contacts: b.contacts?.length ? b.contacts : [{ contact_id: "", name: "", role: "", phone: "", email: "", is_primary: true }],
          bank_account_name: bk.bank_account_name || "", bank_account_number: bk.bank_account_number || "",
          bank_ifsc: bk.bank_ifsc || "", bank_name: bk.bank_name || "", bank_branch: bk.bank_branch || "",
          terms: b.terms || {},
          restrictions: {
            blocked_states: (b.restrictions || {}).blocked_states || [],
            blocked_pincodes: ((b.restrictions || {}).blocked_pincodes || []).join(", "),
            blocked_prefixes: ((b.restrictions || {}).blocked_prefixes || []).join(", "),
            reason: (b.restrictions || {}).reason || "",
          },
        });
      }).catch(e => toast.error("Failed to load brand"));
    }
  }, [brandId]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setNested = (section, key, val) => setForm(f => ({ ...f, [section]: { ...f[section], [key]: val } }));
  const toggle = (sec) => setExpanded(e => ({ ...e, [sec]: !e[sec] }));

  const save = async (andSubmit = false) => {
    setSaving(true);
    setErrors([]);
    const payload = {
      display_name: form.display_name, owner_type: form.owner_type,
      logo_url: form.logo_url, categories: form.categories,
      description: form.description, website: form.website,
      country_of_origin: form.country_of_origin, year_started: form.year_started,
      already_selling_in: form.already_selling_in, already_present_note: form.already_present_note,
      contacts: form.contacts.filter(c => c.name || c.phone),
    };
    if (canKyc) {
      Object.assign(payload, {
        legal_name: form.legal_name, entity_type: form.entity_type,
        gst_number: form.gst_number, pan_number: form.pan_number,
        fssai_number: form.fssai_number, fssai_expiry: form.fssai_expiry,
        trademark_number: form.trademark_number, registered_address: form.registered_address,
        manufacturer_name: form.manufacturer_name, manufacturer_address: form.manufacturer_address,
        marketer_name: form.marketer_name, marketer_address: form.marketer_address,
        bank_account_name: form.bank_account_name, bank_account_number: form.bank_account_number,
        bank_ifsc: form.bank_ifsc, bank_name: form.bank_name, bank_branch: form.bank_branch,
      });
    }
    if (canTerms) payload.terms = form.terms;
    // Parse restrictions
    const pincodes = (form.restrictions.blocked_pincodes || "").split(/[,\s\n]+/).filter(p => p.trim());
    const prefixes = (form.restrictions.blocked_prefixes || "").split(/[,\s\n]+/).filter(p => p.trim());
    payload.restrictions = {
      blocked_states: form.restrictions.blocked_states || [],
      blocked_pincodes: pincodes, blocked_prefixes: prefixes,
      reason: form.restrictions.reason,
    };
    try {
      let res;
      if (isEdit) {
        res = await api.put(`/brands/${brandId}`, payload);
      } else {
        res = await api.post("/brands", payload);
      }
      const id = res.data._id || brandId;
      toast.success(isEdit ? "Brand saved" : "Brand created");
      if (andSubmit) {
        const sr = await api.post(`/brands/${id}/submit`);
        toast.success(sr.data.message);
      }
      navigate(`/brands/${id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.errors) {
        setErrors(detail.errors);
        toast.error("Please fix the errors below");
      } else {
        const msg = typeof detail === "string" ? detail : "Save failed";
        setErrors([msg]);
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const Section = ({ id, title, children, hidden }) => {
    if (hidden) return null;
    const open = expanded[id];
    return (
      <div className="border rounded-md mb-3" data-testid={`section-${id}`}>
        <button className="w-full flex items-center justify-between px-4 py-2.5 text-left" onClick={() => toggle(id)}>
          <span className="text-sm font-semibold text-zinc-800">{title}</span>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
      </div>
    );
  };

  const Field = ({ label, value, onChange, required, placeholder, type = "text", testId }) => (
    <div>
      <Label className="text-xs text-zinc-500">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
      <Input className="h-8 text-xs mt-0.5" type={type} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} />
    </div>
  );

  const states = (getItems("indian_states") || []).map(s => s.value || s.key);
  const [catOptions, setCatOptions] = useState([]);
  useEffect(() => { api.get("/brands/categories").then(r => setCatOptions(r.data || [])).catch(() => {}); }, []);
  const badPincodes = (form.restrictions.blocked_pincodes || "").split(/[,\s\n]+/).filter(p => p.trim() && !/^\d{6}$/.test(p.trim()));
  const badPrefixes = (form.restrictions.blocked_prefixes || "").split(/[,\s\n]+/).filter(p => p.trim() && !/^\d{3}$/.test(p.trim()));
  const entityTypes = (getItems("brand_entity_types") || []).map(s => s.value || s.key);
  const contactRoles = (getItems("brand_contact_roles") || []).map(s => s.value || s.key);
  const channels = (getItems("brand_existing_channels") || []).map(s => s.value || s.key);

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="brand-form-page">
      <h1 className="text-xl font-bold text-zinc-900">{isEdit ? `Edit Brand` : "New Brand"}</h1>
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3" data-testid="form-errors">
          <p className="text-sm font-medium text-red-700 mb-1">Please fix:</p>
          {errors.map((e, i) => <p key={i} className="text-xs text-red-600">- {e}</p>)}
        </div>
      )}

      <Section id="identity" title="A. Brand Identity">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Display Name" value={form.display_name} onChange={v => set("display_name", v)} required testId="input-display-name" />
          <div>
            <Label className="text-xs text-zinc-500">Owner Type <span className="text-red-500">*</span></Label>
            <select className="w-full h-8 text-xs border rounded-md px-2 mt-0.5" value={form.owner_type} onChange={e => set("owner_type", e.target.value)} data-testid="select-owner-type">
              <option value="skippi">SKIPPI</option>
              <option value="third_party">Third Party</option>
            </select>
          </div>
        </div>
        <Field label="Description" value={form.description} onChange={v => set("description", v)} testId="input-description" />
        <div>
          <Label className="text-xs text-zinc-500">Categories (from Product Catalogue)</Label>
          <div className="flex flex-wrap gap-2 mt-1 border rounded-md p-2 max-h-28 overflow-y-auto" data-testid="brand-categories">
            {catOptions.length === 0 && <span className="text-xs text-zinc-400">No catalogue categories yet</span>}
            {catOptions.map(c => (
              <label key={c.category_id} className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={(form.categories || []).includes(c.category_id)} onChange={e => {
                  const arr = [...(form.categories || [])];
                  if (e.target.checked) arr.push(c.category_id); else arr.splice(arr.indexOf(c.category_id), 1);
                  set("categories", arr);
                }} />
                {c.name}
              </label>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Website" value={form.website} onChange={v => set("website", v)} testId="input-website" />
          <Field label="Country of Origin" value={form.country_of_origin} onChange={v => set("country_of_origin", v)} testId="input-country" />
          <Field label="Year Started" value={form.year_started} onChange={v => set("year_started", v)} type="number" testId="input-year" />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Already Selling In</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {channels.map(ch => (
              <label key={ch} className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={(form.already_selling_in || []).includes(ch)} onChange={e => {
                  const arr = [...(form.already_selling_in || [])];
                  if (e.target.checked) arr.push(ch); else arr.splice(arr.indexOf(ch), 1);
                  set("already_selling_in", arr);
                }} />
                {ch}
              </label>
            ))}
          </div>
        </div>
      </Section>

      <Section id="company" title="B. Company & Compliance" hidden={!canKyc}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Legal Name" value={form.legal_name} onChange={v => set("legal_name", v)} required testId="input-legal-name" />
          <div>
            <Label className="text-xs text-zinc-500">Entity Type</Label>
            <select className="w-full h-8 text-xs border rounded-md px-2 mt-0.5" value={form.entity_type} onChange={e => set("entity_type", e.target.value)}>
              <option value="">Select...</option>
              {entityTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <Field label="GST Number" value={form.gst_number} onChange={v => set("gst_number", v)} required testId="input-gst" />
          <Field label="PAN" value={form.pan_number} onChange={v => set("pan_number", v)} testId="input-pan" />
          <Field label="FSSAI Number" value={form.fssai_number} onChange={v => set("fssai_number", v)} testId="input-fssai" />
          <Field label="FSSAI Expiry" value={form.fssai_expiry} onChange={v => set("fssai_expiry", v)} type="date" />
          <Field label="Trademark No." value={form.trademark_number} onChange={v => set("trademark_number", v)} />
        </div>
        <Field label="Registered Address" value={form.registered_address} onChange={v => set("registered_address", v)} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Manufacturer Name" value={form.manufacturer_name} onChange={v => set("manufacturer_name", v)} />
          <Field label="Manufacturer Address" value={form.manufacturer_address} onChange={v => set("manufacturer_address", v)} />
          <Field label="Marketer Name" value={form.marketer_name} onChange={v => set("marketer_name", v)} />
          <Field label="Marketer Address" value={form.marketer_address} onChange={v => set("marketer_address", v)} />
        </div>
      </Section>

      <Section id="contacts" title="C. Contacts" hidden={!canContacts}>
        {form.contacts.map((c, i) => (
          <div key={i} className="flex gap-2 items-end border-b pb-2 mb-2">
            <div className="flex-1"><Field label="Name" value={c.name} onChange={v => { const arr = [...form.contacts]; arr[i] = {...arr[i], name: v}; set("contacts", arr); }} required={c.is_primary} testId={`contact-name-${i}`} /></div>
            <div className="w-28">
              <Label className="text-xs text-zinc-500">Role</Label>
              <select className="w-full h-8 text-xs border rounded-md px-1 mt-0.5" value={c.role} onChange={e => { const arr = [...form.contacts]; arr[i] = {...arr[i], role: e.target.value}; set("contacts", arr); }}>
                <option value="">Select...</option>
                {contactRoles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="flex-1"><Field label="Phone" value={c.phone} onChange={v => { const arr = [...form.contacts]; arr[i] = {...arr[i], phone: v}; set("contacts", arr); }} required={c.is_primary} testId={`contact-phone-${i}`} /></div>
            <div className="flex-1"><Field label="Email" value={c.email} onChange={v => { const arr = [...form.contacts]; arr[i] = {...arr[i], email: v}; set("contacts", arr); }} /></div>
            <label className="flex items-center gap-1 text-xs whitespace-nowrap">
              <input type="radio" name="primary_contact" checked={c.is_primary} onChange={() => { const arr = form.contacts.map((x, j) => ({...x, is_primary: j === i})); set("contacts", arr); }} /> Primary
            </label>
            {form.contacts.length > 1 && <button className="text-red-400 hover:text-red-600" onClick={() => set("contacts", form.contacts.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={() => set("contacts", [...form.contacts, { contact_id: "", name: "", role: "", phone: "", email: "", is_primary: false }])} data-testid="add-contact-btn"><Plus className="w-3 h-3 mr-1" /> Add Contact</Button>
      </Section>

      <Section id="bank" title="D. Bank Details" hidden={!canKyc}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Account Name" value={form.bank_account_name} onChange={v => set("bank_account_name", v)} />
          <Field label="Account Number" value={form.bank_account_number} onChange={v => set("bank_account_number", v)} />
          <Field label="IFSC" value={form.bank_ifsc} onChange={v => set("bank_ifsc", v)} />
          <Field label="Bank" value={form.bank_name} onChange={v => set("bank_name", v)} />
          <Field label="Branch" value={form.bank_branch} onChange={v => set("bank_branch", v)} />
        </div>
      </Section>

      <Section id="terms" title="E. Commercial Terms" hidden={!canTerms}>
        <div className="bg-zinc-50 border rounded-sm p-2 mb-3 text-xs text-zinc-500">Optional. Recorded for reference only — ORBIT does not enforce these terms.</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-500">Buying Model</Label>
            <select className="w-full h-8 text-xs border rounded-md px-2 mt-0.5" value={form.terms.buying_model || ""} onChange={e => setNested("terms", "buying_model", e.target.value)}>
              <option value="">Select...</option>
              <option value="sor">SOR</option>
              <option value="outright">Outright</option>
              <option value="to_be_decided">To Be Decided</option>
            </select>
          </div>
          <Field label="Payment Terms (days)" value={form.terms.payment_terms_days} onChange={v => setNested("terms", "payment_terms_days", parseInt(v) || 0)} type="number" />
          <Field label="MDF %" value={form.terms.mdf_percent} onChange={v => setNested("terms", "mdf_percent", parseFloat(v) || 0)} type="number" />
          <Field label="Return Cap %" value={form.terms.return_cap_percent} onChange={v => setNested("terms", "return_cap_percent", parseFloat(v) || 0)} type="number" />
          <Field label="Return Window (days)" value={form.terms.return_window_days} onChange={v => setNested("terms", "return_window_days", parseInt(v) || 0)} type="number" />
        </div>
      </Section>

      <Section id="restrictions" title="F. Where NOT to Sell">
        <p className="text-xs text-zinc-500 mb-2">Use pincode areas for border districts — a DP's state may differ from the pincode's state.</p>
        <div>
          <Label className="text-xs text-zinc-500">Blocked States</Label>
          <div className="flex flex-wrap gap-1 mt-1 max-h-32 overflow-y-auto border rounded-md p-2">
            {states.map(st => (
              <label key={st} className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={(form.restrictions.blocked_states || []).includes(st)} onChange={e => {
                  const arr = [...(form.restrictions.blocked_states || [])];
                  if (e.target.checked) arr.push(st); else arr.splice(arr.indexOf(st), 1);
                  setForm(f => ({...f, restrictions: {...f.restrictions, blocked_states: arr}}));
                }} />
                {st}
              </label>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-xs text-zinc-500">Blocked Pincodes (6-digit, comma-separated)</Label>
            <textarea className="w-full border rounded-md p-2 text-xs h-16 mt-0.5" value={form.restrictions.blocked_pincodes || ""} onChange={e => setForm(f => ({...f, restrictions: {...f.restrictions, blocked_pincodes: e.target.value}}))} data-testid="input-blocked-pincodes" />
            {badPincodes.length > 0 && <p className="text-[11px] text-red-600 mt-1" data-testid="bad-pincodes">Not a 6-digit pincode and will be ignored: {badPincodes.join(", ")}</p>}
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Blocked Pincode Areas (3-digit prefixes)</Label>
            <textarea className="w-full border rounded-md p-2 text-xs h-16 mt-0.5" value={form.restrictions.blocked_prefixes || ""} onChange={e => setForm(f => ({...f, restrictions: {...f.restrictions, blocked_prefixes: e.target.value}}))} data-testid="input-blocked-prefixes" />
            {badPrefixes.length > 0 && <p className="text-[11px] text-red-600 mt-1" data-testid="bad-prefixes">Not a 3-digit area and will be ignored: {badPrefixes.join(", ")}</p>}
          </div>
        </div>
        <Field label="Reason" value={form.restrictions.reason} onChange={v => setForm(f => ({...f, restrictions: {...f.restrictions, reason: v}}))} />
      </Section>

      <div className="sticky bottom-0 bg-white border-t py-3 flex gap-2 justify-end" data-testid="brand-form-actions">
        <Button variant="outline" onClick={() => navigate(brandId ? `/brands/${brandId}` : "/brands")}>Cancel</Button>
        <Button onClick={() => save(false)} disabled={saving} data-testid="save-draft-btn">{saving ? "Saving..." : "Save Draft"}</Button>
        <Button onClick={() => save(true)} disabled={saving} data-testid="submit-btn" className="bg-emerald-600 hover:bg-emerald-700">{saving ? "Saving..." : "Submit for Approval"}</Button>
      </div>
    </div>
  );
}
